/**
 * Environment Switcher
 *
 * Vite MODE decides which endpoints to use (never let .env.local poison a prod build):
 *   npm run dev          → MODE=development → VITE_DEV_*
 *   npm run build        → MODE=production  → VITE_PROD_*
 *   npm run build:preprod → MODE=development → VITE_DEV_*
 *
 * Env file layout (see zDocs/ENV-FRONTEND.md):
 *   .env                     — production defaults (safe to use for `npm run build`)
 *   .env.development.local   — ALL local/dev overrides (NOT loaded by production builds)
 *   .env.local               — leave empty / no VITE_APP_ENV / no VITE_DEV_* (Vite loads it always)
 */

const environments = {
  development: {
    fhirUrl: 'https://dev-fhir.mieros.cloud',
    apiUrl: 'https://dev-mieapi.mieros.cloud',
    paymentUrl: 'https://dev-payments.mieros.cloud',
  },
  production: {
    fhirUrl: 'https://fhir.mieros.cloud',
    apiUrl: 'https://mieapi.mieros.cloud',
    paymentUrl: 'https://payments.mieros.cloud',
  },
};

const getEnvVar = (key) => {
  return import.meta.env[`VITE_${key}`] || import.meta.env[key];
};

const getMode = () => {
  return import.meta.env.MODE || 'development';
};

/**
 * Always follow Vite MODE for API/FHIR/payment selection.
 * Ignoring VITE_APP_ENV here prevents .env.local (loaded in every mode) from
 * shipping a production bundle that still points at dev-mieapi.
 */
const getEnvironment = () => {
  const mode = getMode();
  const isDev = mode !== 'production';
  const envName = isDev ? 'development' : 'production';
  const base = environments[envName];

  const fhirUrl = isDev
    ? (getEnvVar('DEV_FHIR_ENDPOINT') || base.fhirUrl)
    : (getEnvVar('PROD_FHIR_ENDPOINT') || base.fhirUrl);
  const apiUrl = isDev
    ? (getEnvVar('DEV_BLOCKCHAIN_ENDPOINT') || getEnvVar('DEV_API_ENDPOINT') || base.apiUrl)
    : (getEnvVar('PROD_BLOCKCHAIN_ENDPOINT') || getEnvVar('PROD_API_ENDPOINT') || base.apiUrl);
  const paymentUrl = isDev
    ? (getEnvVar('DEV_PAYMENT_ENDPOINT') || base.paymentUrl)
    : (getEnvVar('PROD_PAYMENT_ENDPOINT') || base.paymentUrl);

  return {
    ...base,
    fhirUrl,
    apiUrl,
    paymentUrl,
  };
};

const localLlmUrl = getEnvVar('LOCAL_LLM_URL') || 'https://chat.mieros.cloud/gptj';
const localLlmTimeoutMs = Number(getEnvVar('LOCAL_LLM_TIMEOUT_MS')) || 180000;
const preferLocalLlm = (getEnvVar('AI_PREFER_LOCAL') || 'true') !== 'false';

const config = {
  ...getEnvironment(),
  environment: getMode() === 'production' ? 'production' : 'development',
  isDev: () => getMode() !== 'production',
  isProd: () => getMode() === 'production',
  fhirSystemBase: getEnvVar('FHIR_ID_SYSTEM_BASE') || 'https://fhir.mieros.cloud',
  localLlmUrl,
  localLlmTimeoutMs,
  preferLocalLlm,
};

export default config;
